import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { SessionProps, useAuth } from 'hooks/use-auth';
import { useConfirmEmail } from 'hooks/use-confirm-email';
import { useLogin } from 'hooks/use-login';
import { toast } from 'react-toastify';
import { SESSION_EVENT } from 'utils/constants';
import { Event } from 'utils/helpers';

export const useLoginByEmailHook = () => {
  const [isProceedToValidateEmail, setIsProceedToValidateEmail] =
    useState(true);
  const { isLoading } = useConfirmEmail();
  const { mutate: loginSession } = useLogin();

  const { t } = useTranslation();
  const {
    session,
    isLoginByEmailOpen,
    setAuthSession,
    handleIsOpenLoginModal,
    isUserDuplicateOpen,
    handleIsOpenLoginByEmailModal,
    handleIsOpenCheckOTPCodeOpen,
    handleChangeStartedLoginByEmailOrSMS,
  } = useAuth();

  useEffect(() => {
    if (isUserDuplicateOpen) {
      setIsProceedToValidateEmail(false);
    }
  }, [isUserDuplicateOpen]);

  const proceedToLogin = (params: {
    email: string;
    userDuplicated?: boolean;
  }) => {
    if (!params.userDuplicated) {
      Event.push('getin_login_email_click');
      handleChangeStartedLoginByEmailOrSMS(SESSION_EVENT.LOGIN_BY_EMAIL);
    }

    loginSession(
      {
        currentFlow: 'email',
        params: {
          email: params.email,
        },
      },
      {
        onSuccess: ({ data }) => {
          setAuthSession({
            ...data,
            registerSession: !params.userDuplicated
              ? data?.session
              : session?.registerSession,
            waiting_time: Number(data.waiting_time),
            phone: session?.phone,
            email: params.email,
          });

          handleIsOpenLoginByEmailModal(false);
          handleIsOpenCheckOTPCodeOpen(true);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          setAuthSession({
            ...session,
            waiting_time: 0,
          } as SessionProps);

          toast.error(
            error?.response?.data?.errors?.general ||
              error?.response?.data?.message ||
              'Erro ao enviar os dados. Por favor, tente novamente.'
          );
        },
      }
    );
  };

  const schema = Yup.object().shape({
    email: Yup.string()
      .email(t('@Login.email-not-valid'))
      .trim()
      .required(t('@Login.email-required')),
  });

  const { values, handleChange, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: schema,
    onSubmit: proceedToLogin,
  });

  const formikProps = (field: keyof Omit<typeof values, 'userDuplicated'>) => ({
    value: values[field],
    error: touched[field] && errors[field] ? errors[field] : '',
    onChange: handleChange(field),
  });

  const handleContinueBySMS = () => {
    handleIsOpenLoginByEmailModal(false);
    handleIsOpenLoginModal(true);
  };

  const handleCloseModal = () => {
    setAuthSession(null);
    handleIsOpenLoginByEmailModal(false);
  };

  return {
    isProceedToValidateEmail,
    isLoginByEmailOpen,
    loading: isLoading,
    proceedToLogin,
    formikProps,
    handleCloseModal,
    handleContinueBySMS,
    handleSubmit,
    t,
  };
};
